import { useRef, useEffect, useMemo } from "react";

import { toPixels } from "../../../../utils/css";

export function useTextAligner<E extends HTMLElement = HTMLElement>(textCenter: boolean, customProperty = "--button-text-offset") {
  const contentRef = useRef<E>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const style = useMemo(() => (textCenter ? { transform: `var(${customProperty})` } : { transform: "translateX(0)" }), [textCenter]);
  const buttonProps = useMemo(() => ({ ref: buttonRef }), []);
  const contentProps = useMemo(() => ({ ref: contentRef, style }), [style]);

  useEffect(() => {
    const container = buttonRef.current;
    const content = contentRef.current;

    if (!container || !content) {
      if (content) {
        content.style.setProperty(customProperty, "translateX(0)");
      }

      return;
    }

    let containerWidth = container.offsetWidth;
    let contentWidth = content.offsetWidth;

    const observer = new ResizeObserver(entries => {
      for (const entry of entries) {
        if (entry.target === container) {
          containerWidth = entry.borderBoxSize[0]?.inlineSize ?? 0;
          continue;
        }

        if (entry.target === content) {
          contentWidth = entry.borderBoxSize[0]?.inlineSize ?? 0;
        }
      }

      if (!(contentWidth && containerWidth)) {
        content.style.setProperty(customProperty, "translateX(0)");
      } else {
        const textOffset = Math.round(containerWidth / 2) - Math.round(contentWidth / 2);
        content.style.setProperty(customProperty, `translateX(${toPixels(textOffset)})`);
      }
    });

    observer.observe(container);
    observer.observe(content);

    return () => {
      observer.unobserve(container);
      observer.unobserve(content);
    };
  }, []);

  return { buttonProps, contentProps };
}
